import cn from 'classnames';
import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusTagField } from '../../../../components/ui/status-tag';
import { formatDateLabel } from '../../../../utils/format';
import { toUpCase } from '../../../../utils/misc';
import styles from '../edit.module.scss';

interface ApplicationSummaryProps {
    data?: any;
    paymentStatus?: any;
    heading?: ReactNode;
    className?: string;
}

type SummaryItem = {
    key: string;
    label: ReactNode;
    value: ReactNode;
    className?: string;
};

const normalizeValue = (value: unknown, fallback = '—'): ReactNode => {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }

    if (Array.isArray(value)) {
        return value.length ? value.join(', ') : fallback;
    }

    return value as ReactNode;
};

const buildApplicantName = (application?: any) => {
    if (!application) return undefined;

    return (
        application.applicant_name ??
        application.representative_name ??
        application.applicant?.first_name ??
        application.applicant?.name ??
        application.applicant?.full_name
    );
};

export const ApplicationSummary: FC<ApplicationSummaryProps> = ({
    data,
    paymentStatus,
    heading,
    className,
}) => {
    const { t } = useTranslation();

    const stateTaxLabel = t('applications.fields.state_tax');
    const expertiseLabel = t('applications.fields.expertise');
    const mohNumberLabel = t(
        'applications.fields.moh_number_with_date',
        'Номер та дата МОЗ'
    );

    const applicationNumber =
        data?.registration_certificate_number ?? data?.moh_application_number;
    const applicationDate = formatDateLabel(
        data?.registration_certificate_date,
        ''
    );

    const applicationDateSuffix = applicationDate
        ? `${t(
              'applications.wizard.application_date_suffix'
          )} ${applicationDate}`
        : '';
    const mohNumberValue = applicationNumber
        ? `№ ${applicationNumber}${
              applicationDateSuffix ? ` ${applicationDateSuffix}` : ''
          }`
        : '—';

    const statusTag = useMemo(() => {
        const statusName = data?.status?.name;
        const color = data?.status?.color;

        return statusName ? (
            <StatusTagField
                value={statusName}
                style={color ? { backgroundColor: color } : undefined}
            />
        ) : (
            '—'
        );
    }, [data?.status]);

    const expertiseValue = paymentStatus?.expertise_status
        ? toUpCase(paymentStatus.expertise_status)
        : null;

    const stateTaxValue = paymentStatus?.state_collection_status
        ? toUpCase(paymentStatus.state_collection_status)
        : null;

    const items: Array<SummaryItem | null> = [
        {
            key: 'name',
            label: t('applications.fields.name'),
            value: normalizeValue(data?.name),
            className: styles.infoItemThird,
        },
        {
            key: 'updated',
            label: t('applications.fields.updated_at'),
            value: formatDateLabel(data?.updated_at),
            className: styles.infoItemThird,
        },
        {
            key: 'moz_number',
            label: mohNumberLabel,
            value: normalizeValue(mohNumberValue),
            className: styles.infoItemThird,
        },
        {
            key: 'status',
            label: t('applications.fields.status'),
            value: statusTag,
            className: styles.infoItemThird,
        },
        {
            key: 'procedure',
            label: t('applications.fields.procedure'),
            value: normalizeValue(data?.type?.name),
            className: styles.infoItemThird,
        },
        {
            key: 'uuid',
            label: 'UUID',
            value: normalizeValue(data?.guid),
            className: styles.infoItemFull,
        },
        {
            key: 'applicant',
            label: t('applications.fields.applicant'),
            value: normalizeValue(buildApplicantName(data)),
            className: styles.infoItemFull,
        },
        paymentStatus
            ? {
                  key: 'expertise',
                  label: expertiseLabel,
                  value: normalizeValue(expertiseValue),
                  className: styles.infoItemHalf,
              }
            : null,
        paymentStatus
            ? {
                  key: 'state_tax',
                  label: stateTaxLabel,
                  value: normalizeValue(stateTaxValue),
                  className: styles.infoItemHalf,
              }
            : null,
    ];

    const card = (
        <section
            className={cn(
                styles.sectionCard,
                styles.applicationSummary,
                className
            )}
        >
            <div className={styles.infoGrid}>
                {items
                    .filter((item): item is SummaryItem => Boolean(item))
                    .map(item => (
                        <div
                            key={item.key}
                            className={cn(styles.infoItem, item.className)}
                        >
                            <span className={styles.infoLabel}>
                                {item.label}
                            </span>
                            <span className={styles.infoValue}>
                                {item.value}
                            </span>
                        </div>
                    ))}
            </div>
        </section>
    );

    if (!heading) {
        return card;
    }

    return (
        <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>{heading}</h2>
            {card}
        </div>
    );
};

export default ApplicationSummary;
